import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

import { authApi, LoginRequest, RegisterRequest, PasswordChangeRequest, ProfileUpdateRequest } from '@/api/auth'
import { useAuthStore } from '@/store/auth'
import { webSocketService } from '@/services/websocket'

// Login Hook
export const useLogin = () => {
  const navigate = useNavigate()
  const { setAuth, setLoading, setError } = useAuthStore()

  const mutation = useMutation({
    mutationFn: authApi.login,
    onMutate: (variables) => {
      setLoading(true)
      // Don't clear error immediately, let it persist until success or new error
    },
    onSuccess: (data) => {
      // Clear any previous errors only on success
      setError(null)
      
      // Store auth data
      setAuth(data.user, {
        access_token: data.tokens.access_token,
        refresh_token: data.tokens.refresh_token,
        token_type: data.tokens.token_type,
      })
      
      // Set up WebSocket connection with user info
      webSocketService.setCurrentUser(data.user);
      webSocketService.connect(data.tokens.access_token);
      
      setLoading(false)
      toast.success(`Willkommen, ${data.user.name}!`)
      
      // Extra role mapping for safety (in case backend doesn't map properly)
      const userRole = data.user.role as string;
      const mappedRole = userRole === 'call_center_agent' ? 'staff' : userRole;
      
      // Force password change on first login after migration
      if (data.must_change_password) {
        navigate('/auth/set-password')
        return
      }

      // Redirect based on user role
      if (mappedRole === 'admin') {
        navigate('/admin')
      } else if (mappedRole === 'callcenter_admin') {
        navigate('/callcenter')
      } else if (mappedRole === 'staff') {
        navigate('/callcenter')
      } else {
        navigate('/dashboard')
      }
    },
    onError: (error: any) => {
      setLoading(false)
      const errorMessage = error.response?.data?.detail || 'Fehler beim Anmelden. Bitte überprüfen Sie Ihre Anmeldedaten.'
      
      setError(errorMessage)
      // Show error via toast only
      toast.error(errorMessage, {
        duration: 6000,
        id: 'login-error'
      })
    },
  })

  return mutation;
}

// Register Hook
export const useRegister = () => {
  const navigate = useNavigate()
  const { setLoading, setError } = useAuthStore()

  return useMutation({
    mutationFn: authApi.register,
    onMutate: () => {
      setLoading(true)
      setError(null)
    },
    onSuccess: (data) => {
      setLoading(false)
      toast.success('Registrierung erfolgreich! Bitte melden Sie sich an.')
      navigate('/auth/login')
    },
    onError: (error: any) => {
      setLoading(false)
      const errorMessage = error.response?.data?.detail || 'Fehler bei der Registrierung.'
      setError(errorMessage)
      toast.error(errorMessage)
    },
  })
}

// Logout Hook
export const useLogout = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { logout } = useAuthStore()

  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      // Clear auth state
      logout()
      
      // Clear all queries
      queryClient.clear()
      
      toast.success('Erfolgreich abgemeldet.')
      navigate('/auth/login')
    },
    onError: () => {
      // Force logout even if API call fails
      logout()
      queryClient.clear()
      navigate('/auth/login')
    },
  })
}

// Get Current User Hook
export const useCurrentUser = () => {
  const { isAuthenticated, user } = useAuthStore()

  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authApi.me,
    enabled: isAuthenticated && !user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Token Validation Hook
export const useValidateToken = () => {
  const { isAuthenticated } = useAuthStore()

  return useQuery({
    queryKey: ['auth', 'validate'],
    queryFn: authApi.validate,
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 15 * 60 * 1000, // Refetch every 15 minutes
  })
}

// Change Password Hook
export const useChangePassword = () => {
  const { setLoading, setError } = useAuthStore()

  return useMutation({
    mutationFn: authApi.changePassword,
    onMutate: () => {
      setLoading(true)
      setError(null)
    },
    onSuccess: () => {
      setLoading(false)
      toast.success('Ihr Passwort wurde erfolgreich geändert.')
    },
    onError: (error: any) => {
      setLoading(false)
      const errorMessage = error.response?.data?.detail || 'Fehler beim Ändern des Passworts.'
      setError(errorMessage)
      toast.error(errorMessage)
    },
  })
}

// Update Profile Hook
export const useUpdateProfile = () => {
  const queryClient = useQueryClient()
  const { setUser, setLoading, setError } = useAuthStore()

  return useMutation({
    mutationFn: authApi.updateProfile,
    onMutate: () => {
      setLoading(true)
      setError(null)
    },
    onSuccess: (data) => {
      setLoading(false)
      setUser(data)
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      
      toast.success('Ihre Profilinformationen wurden aktualisiert.')
    },
    onError: (error: any) => {
      setLoading(false)
      const errorMessage = error.response?.data?.detail || 'Fehler beim Aktualisieren des Profils.'
      setError(errorMessage)
      toast.error(errorMessage)
    },
  })
}

// Password Reset Request Hook
export const useRequestPasswordReset = () => {
  return useMutation({
    mutationFn: authApi.requestPasswordReset,
    onSuccess: () => {
      toast.success('Passwort-Reset-Link wurde an Ihre E-Mail-Adresse gesendet.')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail || 'Passwort-Reset-Anfrage konnte nicht gesendet werden.'
      toast.error(errorMessage)
    },
  })
}

// Password Reset Hook
export const useResetPassword = () => {
  const navigate = useNavigate()

  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authApi.resetPassword(token, password),
    onSuccess: () => {
      toast.success('Ihr Passwort wurde erfolgreich zurückgesetzt. Bitte melden Sie sich an.')
      navigate('/auth/login')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail || 'Fehler beim Zurücksetzen des Passworts.'
      toast.error(errorMessage)
    },
  })
}

// Check Email Hook
export const useCheckEmail = () => {
  return useMutation({
    mutationFn: authApi.checkEmail,
  })
}

// Custom hook for auth status
export const useAuthStatus = () => {
  const { isAuthenticated, user, isLoading, error, isAdmin, isStaff, isCustomer, hasRole } = useAuthStore()

  return {
    isAuthenticated,
    user,
    isLoading,
    error,
    isAdmin: isAdmin(),
    isStaff: isStaff(),
    isCustomer: isCustomer(),
    hasRole,
  }
}

// Main useAuth Hook - combines all auth functionality
export const useAuth = () => {
  const loginMutation = useLogin()
  const registerMutation = useRegister()
  const logoutMutation = useLogout()
  const changePasswordMutation = useChangePassword()
  const updateProfileMutation = useUpdateProfile()
  const requestPasswordResetMutation = useRequestPasswordReset()
  const resetPasswordMutation = useResetPassword()
  const checkEmailMutation = useCheckEmail()
  
  const currentUserQuery = useCurrentUser()
  const validateTokenQuery = useValidateToken()
  const authStatus = useAuthStatus()

  return {
    // Mutations
    loginMutation,
    registerMutation,
    logoutMutation,
    changePasswordMutation,
    updateProfileMutation,
    requestPasswordResetMutation,
    resetPasswordMutation,
    checkEmailMutation,
    
    // Queries
    currentUserQuery,
    validateTokenQuery,
    
    // Status
    ...authStatus,
    
    // Convenience methods
    login: loginMutation.mutateAsync,
    register: registerMutation.mutateAsync,
    logout: logoutMutation.mutate,
    changePassword: changePasswordMutation.mutateAsync,
    updateProfile: updateProfileMutation.mutateAsync,
    requestPasswordReset: requestPasswordResetMutation.mutateAsync,
    resetPassword: resetPasswordMutation.mutateAsync,
    checkEmail: checkEmailMutation.mutateAsync,
  }
} 